import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, share, switchMap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CaseManagementService } from '../../services/case-management.service';
import { UtilsService } from '../../edge/utils.service';
import { SharedActions } from '@states/shared/shared.action-types';
import { CaseAccessControlEventActions } from '@states/case-access-control-event/case-access-control-event.actions';

@Injectable()
export class CaseAccessControlEventEffect {
  private readonly actions$ = inject(Actions);
  private readonly caseManagementService = inject(CaseManagementService);
  private readonly utilService = inject(UtilsService);
  private readonly translateService = inject(TranslateService);

  public getEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAccessControlEventActions.getEvents),
      switchMap(({ request }) =>
        this.caseManagementService.getCaseAccessControlEvents(request).pipe(
          map((events) => CaseAccessControlEventActions.getEventsSuccess({ events })),
          catchError((error) => [
            CaseAccessControlEventActions.getEventsFail(),
            SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
          ]),
        ),
      ),
      share(),
    ),
  );

  public copyEventsToCase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CaseAccessControlEventActions.copyEventsToCase),
      switchMap(({ request }) => {
        const { commentText, ...requestPayload } = request;
        const normalizedCommentText = commentText?.trim();
        const normalizedRequest = normalizedCommentText
          ? { ...requestPayload, commentText: normalizedCommentText }
          : requestPayload;

        return this.caseManagementService.copyEventsToCase(normalizedRequest).pipe(
          mergeMap((response) => [
            CaseAccessControlEventActions.copyEventsToCaseSuccess({ response }),
            SharedActions.showMessage({ success: this.translateService.instant('CASES.COPY_EVENTS_SUCCESS') }),
          ]),
          catchError((error) => [
            CaseAccessControlEventActions.copyEventsToCaseFail(),
            SharedActions.showMessage({ error: this.utilService.errMessage(error) }),
          ]),
        );
      }),
      share(),
    ),
  );
}
